import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material"
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import dayjs from "dayjs"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useFormatiList } from "../../hooks/formati"
import { useVocabolariList, useVocabolariTipologieList } from "../../hooks/vocabolari"
import { Formato, TipologiaVocabolario, Vocabolario } from "../../types"
import DialogForm from "../DialogForm"
import { slugify } from "../../utils/utils"
import { getIn } from "formik"

export function FormikTextField({
  field,
  form: { touched, errors, submitCount },
  size = "small",
  sx = {},
  ...props
}: {
  field: any
  form: any
  size?: string
  sx?: any
}) {
  const error = !!getIn(errors, field.name) && (getIn(touched, field.name) || submitCount > 0)
  const helper = (getIn(touched, field.name) || submitCount > 0) && getIn(errors, field.name)
  return (
    <TextField
      error={error}
      helperText={helper}
      size={size}
      {...field}
      {...props}
      sx={{
        ...sx,
        // target diretto del FormHelperText
        "& .MuiFormHelperText-root": {
          color: "#757575 !important",
        },
        "& .MuiFormHelperText-root.Mui-disabled": {
          color: "#757575 !important",
        },
        // helper text color errore
        "& .MuiFormHelperText-root.Mui-error": {
          color: "#d32f2f !important",
        },
      }}
    />
  )
}

export function FormikDatePicker({
  field,
  form: { touched, errors, submitCount, setFieldValue, setFieldTouched },
  variant,
  margin,
  fullWidth,
  label,
  size = "small",
  ...props
}: {
  field: any
  form: any
  variant?: "standard" | "outlined" | "filled" | undefined
  margin?: "none" | "dense" | "normal" | undefined
  fullWidth?: boolean
  label?: string
  size?: "small" | "medium" | undefined
}) {
  const { value, onChange, ...rest } = field
  const error = !!getIn(errors, field.name) && (getIn(touched, field.name) || submitCount > 0)

  return (
    <FormControl fullWidth={fullWidth} margin={margin} variant={variant} size={size}>
      <DatePicker
        label={label}
        error={error}
        value={value ? dayjs(value) : null}
        slotProps={{
          field: {
            clearable: true,
            onClear: () => setFieldValue(field.name, null),
            error: error,
            onBlur: () => setFieldTouched(field.name, true),
          },
          textField: {
            size: size,
          },
        }}
        onChange={(date) => {
          console.log("oc", date)
          setFieldValue(field.name, date ? date?.format("YYYY-MM-DD") : date)
        }}
        {...rest}
        {...props}
      />
      {getIn(errors, field.name) && (getIn(touched, field.name) || submitCount > 0) && (
        <FormHelperText error>{getIn(errors, field.name)}</FormHelperText>
      )}
    </FormControl>
  )
}

type SelectProps = {
  field: any
  form: any
  variant?: "standard" | "outlined" | "filled" | undefined
  margin?: "none" | "dense" | "normal" | undefined
  children?: React.ReactNode
  fullWidth?: boolean
  label?: string
  [key: string]: any
  size: "small" | "medium" | undefined
}

export function FormikSelect({ field, form: { touched, errors, submitCount }, size = "small", ...props }: SelectProps) {
  const { variant, margin, children, fullWidth, label, ...allProps } = props
  const { name: fieldName, ...restField } = field
  const error = !!getIn(errors, field.name) && (getIn(touched, field.name) || submitCount > 0)

  const labelId = `${fieldName}-label`
  const selectId = `${fieldName}-select`
  const helperTextId = `${fieldName}-helper-text`
  return (
    <FormControl fullWidth={fullWidth} margin={margin} variant={variant} size={size}>
      {label && (
        <InputLabel id={labelId} error={error}>
          {label}
        </InputLabel>
      )}
      <Select
        id={selectId}
        labelId={labelId}
        size={size}
        label={label}
        error={error}
        name={fieldName}
        {...restField}
        {...allProps}
      >
        {children}
      </Select>
      {getIn(errors, field.name) && (getIn(touched, field.name) || submitCount > 0) && (
        <FormHelperText error id={helperTextId}>
          {getIn(errors, field.name)}
        </FormHelperText>
      )}
    </FormControl>
  )
}

type FormikSelectVocabolarioProps = {
  vocabolario: string
  field: any
  form: any
  variant?: "standard" | "outlined" | "filled" | undefined
  margin?: "none" | "dense" | "normal" | undefined
  fullWidth?: boolean
  label?: string
  [key: string]: any
  size?: "small" | "medium" | undefined
}

export function FormikSelectVocabolario({
  vocabolario,
  field,
  size = "small",
  form: { touched, errors, submitCount },
  ...props
}: FormikSelectVocabolarioProps) {
  const { variant, margin, children, fullWidth, label, ...allProps } = props
  const { data: options } = useVocabolariList({ vocabolario })
  const { value, name: fieldName, ...restField } = field
  const error = !!getIn(errors, field.name) && (getIn(touched, field.name) || submitCount > 0)

  const labelId = `${fieldName}-label`
  const selectId = `${fieldName}-select`
  const helperTextId = `${fieldName}-helper-text`

  return (
    <FormControl fullWidth={fullWidth} margin={margin} variant={variant} size={size}>
      {label && (
        <InputLabel id={labelId} error={error}>
          {label}
        </InputLabel>
      )}
      <Select
        id={selectId}
        size={size}
        label={label}
        labelId={labelId}
        error={error}
        value={value || ""}
        name={fieldName}
        {...restField}
        {...allProps}
      >
        <MenuItem value={""}>
          <span>-</span>
        </MenuItem>
        {options?.results.map((option: Vocabolario, index: number) => (
          <MenuItem key={index} value={option.id}>
            {option.termine_it}
          </MenuItem>
        ))}
      </Select>
      {getIn(errors, field.name) && (getIn(touched, field.name) || submitCount > 0) && (
        <FormHelperText error id={helperTextId}>
          {getIn(errors, field.name)}
        </FormHelperText>
      )}
    </FormControl>
  )
}

type SelectVocabolarioProps = {
  vocabolario: string
  value: string
  onChange: (e: SelectChangeEvent) => void
  variant?: "standard" | "outlined" | "filled" | undefined
  margin?: "none" | "dense" | "normal" | undefined
  fullWidth?: boolean
  label?: string
  error?: string
  [key: string]: any
}

export function SelectVocabolario({ vocabolario, value, onChange, error, ...props }: SelectVocabolarioProps) {
  const { variant, margin, children, fullWidth, label, size, ...allProps } = props

  const { data: options } = useVocabolariList({ vocabolario })
  const { t } = useTranslation()

  const slug = label ? slugify(label) : slugify(vocabolario)

  const labelId = `${slug}-label`
  const selectId = `${slug}-select`
  const helperTextId = `${slug}-helper-text`

  return (
    <FormControl fullWidth={fullWidth} margin={margin} variant={variant} size={size}>
      {label && (
        <InputLabel id={labelId} error={!!error}>
          {label}
        </InputLabel>
      )}
      <Select
        id={selectId}
        labelId={labelId}
        size={size}
        label={label}
        value={value}
        onChange={onChange}
        error={!!error}
        {...allProps}
      >
        <MenuItem value={""}>{t("general.tutti")}</MenuItem>
        {options?.results.map((option: Vocabolario, index: number) => (
          <MenuItem key={index} value={option.id}>
            {option.termine_it}
          </MenuItem>
        ))}
      </Select>
      {error && (
        <FormHelperText id={helperTextId} error>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  )
}

type SelectTipologiaVocabolarioProps = {
  value: string
  onChange: (e: SelectChangeEvent) => void
  variant?: "standard" | "outlined" | "filled" | undefined
  margin?: "none" | "dense" | "normal" | undefined
  fullWidth?: boolean
  label?: string
  size?: "small" | "medium" | undefined
  [key: string]: any
  emptyLabel?: string
  error?: string
  touched?: boolean
  submitCount?: number
}

export function SelectTipologiaVocabolario({
  value,
  onChange,
  emptyLabel,
  variant,
  margin,
  fullWidth,
  label,
  size = "small",
  error,
  ...allProps
}: SelectTipologiaVocabolarioProps) {
  const { data: options } = useVocabolariTipologieList()

  return (
    <FormControl fullWidth={fullWidth} margin={margin} variant={variant} size={size}>
      {label && <InputLabel error={!!error}>{label}</InputLabel>}
      <Select size={size} label={label} value={value} onChange={onChange} error={!!error} {...allProps}>
        <MenuItem value={""}>{emptyLabel || <span>&nbsp;</span>}</MenuItem>
        {options?.map((option: TipologiaVocabolario, index: number) => (
          <MenuItem key={index} value={option[0]}>
            {option[1]}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  )
}

type ForeignAutoCompleteProps<C extends { id: number }> = {
  name?: string
  value: number | undefined
  onChange: (value: number | null) => void
  onBlur?: () => void
  error?: string
  touched?: boolean
  submitCount?: number
  variant?: "standard" | "outlined" | "filled" | undefined
  margin?: "none" | "dense" | "normal" | undefined
  size?: "small" | "medium" | undefined
  fullWidth?: boolean
  label?: string
  search: string
  setSearch: (search: string) => void
  options: C[]
  optionsLoading: boolean
  required?: boolean
  refetchOptions: () => void
  selectedOption: C | undefined
  selectedOptionLoading: boolean
  getOptionLabel: (option: C) => string
  isOptionEqualToValue?: (option: C, value: C) => boolean
  FormComponent?: React.ComponentType<any>
  formComponentProps?: Record<string, any>
  create?: (data: any) => Promise<any>
  initialCreate?: (search: string) => Partial<C>
}

type SelectFormatoProps = {
  emptyLabel?: string
  value: string
  onChange: (e: SelectChangeEvent, o: Formato | undefined) => void
  variant?: "standard" | "outlined" | "filled" | undefined
  margin?: "none" | "dense" | "normal" | undefined
  fullWidth?: boolean
  label?: string
  error?: string
  [key: string]: any
}

export function SelectFormato({
  emptyLabel = "-",
  value,
  size = "small",
  onChange,
  error,
  ...props
}: SelectFormatoProps) {
  const { variant, margin, children, fullWidth, label, ...allProps } = props
  const { data: options } = useFormatiList()

  return (
    <FormControl fullWidth={fullWidth} margin={margin} variant={variant} size={size}>
      {label && <InputLabel error={!!error}>{label}</InputLabel>}
      <Select
        size={size}
        label={label}
        value={value}
        onChange={(e) => {
          const newOption = options?.results.find((option: Formato) => Number(option.id) === Number(e.target.value))
          onChange(e, newOption)
        }}
        error={!!error}
        {...allProps}
      >
        <MenuItem value={""}>{emptyLabel}</MenuItem>
        {options?.results.map((option: Formato, index: number) => (
          <MenuItem key={index} value={option.id} data-unita-misura={option.unita_misura}>
            {option.nome}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  )
}

export function ForeignAutoComplete<C extends { id: number; addLabel?: string }>({
  value,
  onChange,
  onBlur,
  error,
  touched,
  submitCount = 0,
  search,
  setSearch,
  options,
  optionsLoading,
  refetchOptions,
  selectedOption,
  selectedOptionLoading,
  getOptionLabel,
  isOptionEqualToValue,
  required,
  FormComponent,
  formComponentProps,
  size = "small",
  create,
  initialCreate,
  ...props
}: ForeignAutoCompleteProps<C>) {
  const { variant, margin, fullWidth, label } = props
  const [createModalState, createModalActions] = useModalTrigger()
  const isCreatable = useMemo(() => {
    return !!create && !!FormComponent
  }, [create, FormComponent])

  const { t } = useTranslation()

  return (
    <>
      <Autocomplete
        blurOnSelect
        value={selectedOption ?? null}
        fullWidth={fullWidth}
        loading={optionsLoading || selectedOptionLoading}
        size={size}
        sx={{
          // error helper text color
          "& .MuiFormHelperText-root": {
            color: "#d32f2f !important",
          },
        }}
        onChange={(_, value) => {
          if (value?.id === 0) {
            const initialValue = isCreatable && initialCreate ? initialCreate(search) : undefined
            createModalActions.open(initialValue)
            setSearch("")
            return
          }
          onChange(value ? (value as C)?.id : null)
        }}
        onInputChange={(_, value) => {
          setSearch(value)
          refetchOptions()
        }}
        options={options as C[]}
        filterOptions={
          isCreatable
            ? (options, params) => {
                const filter = createFilterOptions<C>()
                const filtered = filter(options, params)
                if (params.inputValue !== "") {
                  //check that the input value is not already in the options
                  const found = options.find((option) => getOptionLabel(option) === params.inputValue)
                  if (!found) {
                    filtered.push({ id: 0, addLabel: `${params.inputValue}` } as unknown as C)
                  }
                }
                return filtered
              }
            : undefined
        }
        getOptionLabel={(option) => (option.addLabel ? "" : getOptionLabel(option))}
        renderOption={(props, option) => {
          const { key, ...rest } = props
          return (
            <li {...rest} key={option.id}>
              {option.addLabel ? (
                <Button variant="contained" color="primary">
                  <span>{t("general.aggiungi")}&nbsp;</span>
                  <b>{option.addLabel}</b>
                </Button>
              ) : (
                getOptionLabel(option)
              )}
            </li>
          )
        }}
        isOptionEqualToValue={
          isOptionEqualToValue ||
          ((option, value) => {
            return option.id === value.id
          })
        }
        renderInput={(params) => {
          return (
            <TextField
              label={label}
              margin={margin}
              variant={variant}
              helperText={error}
              onBlur={onBlur}
              required={required}
              error={!!error && (touched || submitCount > 0)}
              {...params}
            />
          )
        }}
      />
      {!!FormComponent && !!create && (
        <DialogForm<C>
          open={createModalState.isOpen}
          item={createModalState.value}
          hasCancel
          handleClose={() => {
            createModalActions.close()
            onChange(value as number)
          }}
          title={`${t("general.aggiungi")} ${label}`}
          FormComponent={FormComponent}
          formComponentProps={formComponentProps}
          save={(values: C) => {
            return create(values).then((item) => {
              createModalActions.close()
              onChange((item as C)?.id)
            })
          }}
        />
      )}
    </>
  )
}

type ForeignMultiAutoCompleteProps<C extends { id: number }> = {
  name?: string
  value: number[]
  onChange: (value: number[]) => void
  onBlur?: () => void
  error?: string
  touched?: boolean
  submitCount?: number
  variant?: "standard" | "outlined" | "filled" | undefined
  margin?: "none" | "dense" | "normal" | undefined
  size?: "small" | "medium" | undefined
  fullWidth?: boolean
  label?: string
  search: string
  setSearch: (search: string) => void
  options: C[]
  optionsLoading: boolean
  refetchOptions: () => void
  selectedOptions: C[]
  selectedOptionLoading: boolean
  getOptionLabel: (option: C) => string
  isOptionEqualToValue?: (option: C, value: C) => boolean
  FormComponent?: React.ComponentType<any>
  formComponentProps?: Record<string, any>
  create?: (data: any) => Promise<any>
  initialCreate?: (search: string) => Partial<C>
}

export function ForeignMultiAutoComplete<C extends { id: number }>({
  value,
  onChange,
  onBlur,
  error,
  touched,
  submitCount = 0,
  search,
  setSearch,
  options,
  optionsLoading,
  refetchOptions,
  selectedOptions,
  selectedOptionLoading,
  getOptionLabel,
  isOptionEqualToValue,
  FormComponent,
  formComponentProps,
  create,
  initialCreate,

  ...props
}: ForeignMultiAutoCompleteProps<C>) {
  const [createModalState, createModalActions] = useModalTrigger()
  const isCreatable = useMemo(() => {
    return !!create && !!FormComponent
  }, [create, FormComponent])

  const { t } = useTranslation()
  const { variant, margin, fullWidth, label, size } = props

  return (
    <>
      <Autocomplete
        multiple
        value={selectedOptions}
        fullWidth={fullWidth}
        loading={optionsLoading || selectedOptionLoading}
        size={size}
        onChange={(_, value) => {
          const hasAddLabel = value.find((v) => v.id === 0)
          if (hasAddLabel) {
            const initialValue = isCreatable && initialCreate ? initialCreate(search) : undefined
            createModalActions.open(initialValue)
            setSearch("")
            return
          }

          const finalValue = value.map((v) => v.id)
          onChange(finalValue)
        }}
        onInputChange={(_, value) => {
          setSearch(value)
          refetchOptions()
        }}
        options={options as C[]}
        filterOptions={
          isCreatable
            ? (options, params) => {
                const filter = createFilterOptions<C>()
                const filtered = filter(options, params)
                if (params.inputValue !== "") {
                  //check that the input value is not already in the options
                  const found = options.find((option) => getOptionLabel(option) === params.inputValue)
                  if (!found) {
                    filtered.push({ id: 0, addLabel: `${params.inputValue}` } as unknown as C)
                  }
                }
                return filtered
              }
            : undefined
        }
        isOptionEqualToValue={
          isOptionEqualToValue ||
          ((option, value) => {
            return option.id === value.id
          })
        }
        renderInput={(params) => {
          return (
            <TextField
              label={label}
              margin={margin}
              variant={variant}
              error={(touched || submitCount > 0) && !!error}
              helperText={(touched || submitCount > 0) && error}
              onBlur={onBlur}
              {...params}
            />
          )
        }}
        getOptionLabel={(option: any) => (option.addLabel ? "" : getOptionLabel(option))}
        renderOption={(props, option) => {
          const { key, ...rest } = props
          return (
            <li {...rest} key={option.id}>
              {option.addLabel ? (
                <Button variant="contained" color="primary" inert>
                  <span>{t("general.aggiungi")}&nbsp;</span>
                  <b>{option.addLabel}</b>
                </Button>
              ) : (
                getOptionLabel(option)
              )}
            </li>
          )
        }}
      />
      {!!FormComponent && !!create && (
        <DialogForm<C>
          open={createModalState.isOpen}
          item={createModalState.value}
          hasCancel
          handleClose={() => {
            createModalActions.close()
            onChange(value as number[])
          }}
          title={`${t("general.aggiungi")} ${label}`}
          FormComponent={FormComponent}
          formComponentProps={formComponentProps}
          save={(values: C) => {
            return create(values).then((item) => {
              createModalActions.close()
              //remove 0 from value array and replace with the id of the new item
              const newValue = value.filter((v) => v !== 0)
              newValue.push((item as C)?.id)
              onChange(newValue)
            })
          }}
        />
      )}
    </>
  )
}
